import type { AxiosInstance } from 'axios'

import type {
  AtcAdvertiserSuggestion,
  AtcConfigSnapshot,
  AtcPayloadConfig,
  MetaPage,
  WatchlistFormField,
} from '../models/watchlist.types'

import { DEFINITIONS_REQUEST_CONFIG } from './config'

import { appHttp, unwrap, unwrapEnvelope, type ApiEnvelope } from '@/shared/http'

export class CreativeWatchlistApi {
  constructor(private readonly http: AxiosInstance = appHttp) {}

  private atcBase(accountId: number | string, workspaceId: number | string): string {
    return `/accounts/${accountId}/workspaces/${workspaceId}/settings/google-atc-config/`
  }

  private requestConfig(signal?: AbortSignal) {
    return signal ? { ...DEFINITIONS_REQUEST_CONFIG, signal } : DEFINITIONS_REQUEST_CONFIG
  }

  async getAtcConfigSnapshot(
    accountId: number | string,
    workspaceId: number | string,
    signal?: AbortSignal,
  ): Promise<AtcConfigSnapshot> {
    const rows = unwrapEnvelope(
      await this.http.get<
        ApiEnvelope<Array<{ id?: number | string; value?: AtcPayloadConfig[] | AtcPayloadConfig }>>
      >(this.atcBase(accountId, workspaceId), this.requestConfig(signal)),
    )
    const row = rows?.[0]
    const value = row?.value
    return {
      exists: row !== undefined,
      configs: Array.isArray(value) ? value : value ? [value] : [],
    }
  }

  async searchAtcAdvertisers(
    accountId: number | string,
    workspaceId: number | string,
    query: string,
    signal?: AbortSignal,
  ): Promise<AtcAdvertiserSuggestion[]> {
    return (
      unwrap(
        await this.http.get<AtcAdvertiserSuggestion[]>(
          `${this.atcBase(accountId, workspaceId)}advertisers/?q=${encodeURIComponent(query)}`,
          this.requestConfig(signal),
        ),
      ) ?? []
    )
  }

  async saveAtcConfigs(
    accountId: number | string,
    workspaceId: number | string,
    configs: readonly AtcPayloadConfig[],
    hasExistingConfig: boolean,
  ): Promise<unknown> {
    const verb = hasExistingConfig ? 'update' : 'create'
    return unwrapEnvelope(
      await this.http.post(
        `${this.atcBase(accountId, workspaceId)}${verb}/`,
        { value: configs },
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  async deleteAtcConfigs(
    accountId: number | string,
    workspaceId: number | string,
  ): Promise<unknown> {
    return unwrapEnvelope(
      await this.http.post(
        `${this.atcBase(accountId, workspaceId)}delete/`,
        {},
        DEFINITIONS_REQUEST_CONFIG,
      ),
    )
  }

  async getMetaFormFields(signal?: AbortSignal): Promise<WatchlistFormField[]> {
    const rows = unwrapEnvelope(
      await this.http.get<ApiEnvelope<Array<{ value?: { form_fields?: WatchlistFormField[] } }>>>(
        '/generic/get_configurations_customization?key=meta_ads_library_fields',
        this.requestConfig(signal),
      ),
    )
    return rows?.[0]?.value?.form_fields ?? []
  }

  async getMetaPageDetails(signal?: AbortSignal): Promise<MetaPage[]> {
    return (
      unwrapEnvelope(
        await this.http.get<ApiEnvelope<MetaPage[]>>(
          '/get_metadata_meta_page_details',
          this.requestConfig(signal),
        ),
      ) ?? []
    )
  }
}

export const creativeWatchlistApi = new CreativeWatchlistApi()
